import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { supabase } from "@/integrations/supabase/client";
import { useCategoryTree } from "@/hooks/use-category-tree";

export const Route = createFileRoute("/kb/category/$slug")({
  component: KbCategoryPage,
  head: ({ params }) => ({
    meta: [
      { title: `${params.slug} — Knowledge Base` },
      { name: "description", content: `Help articles in the ${params.slug} category.` },
    ],
  }),
});

function KbCategoryPage() {
  const { slug } = Route.useParams();
  const { data: tree, isLoading: catLoading } = useCategoryTree("kb_categories", slug);
  const current = tree?.current;
  const ids = current ? [current.id, ...tree!.descendants.map((d) => d.id)] : [];

  const { data: articles, isLoading } = useQuery({
    queryKey: ["public", "kb-articles", "by-cats", ids],
    enabled: ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kb_articles")
        .select("id,title,slug,excerpt,category_id,views")
        .eq("published", true)
        .in("category_id", ids)
        .order("views", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  if (!catLoading && !current) throw notFound();

  return (
    <PageShell>
      <PageHero
        eyebrow={tree?.ancestors.map((a) => a.name).join(" › ") || "Knowledge Base"}
        title={current?.name ?? slug}
        sub={current?.description ?? "Articles and how-tos in this category."}
      />
      <section className="py-12">
        <div className="mx-auto max-w-5xl px-4 lg:px-8">
          <nav className="mb-6 text-sm text-muted-foreground">
            <Link to="/kb" className="hover:text-foreground">Knowledge base</Link>
            {tree?.ancestors.map((a) => (
              <span key={a.id}>
                {" › "}
                <Link to="/kb/category/$slug" params={{ slug: a.slug }} className="hover:text-foreground">{a.name}</Link>
              </span>
            ))}
            <span> › <span className="text-foreground">{current?.name}</span></span>
          </nav>

          {tree && tree.children.length > 0 && (
            <div className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {tree.children.map((c) => (
                <Link key={c.id} to="/kb/category/$slug" params={{ slug: c.slug }} className="rounded-xl border border-border bg-card p-4 hover:border-primary/40">
                  <div className="text-sm font-semibold">{c.name}</div>
                  {c.description && <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{c.description}</div>}
                </Link>
              ))}
            </div>
          )}

          {(isLoading || catLoading) && <p className="text-center text-muted-foreground">Loading…</p>}
          {!isLoading && !articles?.length && <p className="text-center text-muted-foreground">No articles in this category yet.</p>}

          <div className="space-y-2">
            {articles?.map((a: any) => (
              <Link key={a.id} to="/kb/$slug" params={{ slug: a.slug }} className="block rounded-xl border border-border bg-card p-4 hover:border-primary/40 transition">
                <h3 className="text-base font-semibold">{a.title}</h3>
                {a.excerpt && <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{a.excerpt}</p>}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
